/*
 * submissions — array of Attachment instances (see Processors/Attachment.js)
 *
 * Each Attachment exposes:
 *   fileId: string           — unique identifier, e.g. "file_1"
 *   filename: string
 *   kind: "medical" | "pharmacy"
 *   mapping: { mappings: { [schemaKey]: sourceColumnName } }
 *   columnNames: string[]    — ordered schema column names for this kind
 *   ndcList: { code, manufacturer, product_name }[]
 *   processedData: ProcessedRow[]
 *   summary: Summary
 *
 * ProcessedRow — one claim that matched the NDC list:
 *   data: { [schemaField]: formattedValue }   — formatted output values
 *   rawData: { [schemaField]: rawValue }       — original source values
 *   validations: { field, message, originalValue, processedValue }[]
 *   columns_with_errors: { [field]: true }
 *   hasErrors: boolean
 *   manufacturer: string
 *   product_name: string
 *   line_number: number
 *
 * Summary — produced by the engine after processing:
 *   original: number          — total input rows before NDC filtering
 *   rows: number              — rows that matched the NDC list
 *   valid / invalid: number
 *   manufacturers: {
 *     [name]: { rows, valid, invalid, hasErrors, pharmacies: string[], products: string[], ndcs: string[] }
 *   }
 *   pharmacies: string[]      — unique service_provider_ids across all rows
 *   products: string[]        — unique NDC codes across all rows
 *   entities: { id_340b, rowCount, ndcs: { [ndc]: { rows, ndc, product_name, manufacturer } },
 *               products: string[], manufacturers: string[] }[]
 *
 * ndcList — { code: string, manufacturer: string, product_name: string }[]
 *   The full list passed to the engine; used here to build per-manufacturer NDC sets.
 *
 * mfrNdcs — Set<string>
 *   NDC codes belonging to a single manufacturer; returned by buildMfrNdcs and used
 *   by buildManufacturerFilteredData to filter rows to a selected manufacturer.
 */

export const buildManufacturerStats = (submissions) => {
  const stats = {};
  for (const s of submissions) {
    for (const [mfr, mfrData] of Object.entries(s.summary.manufacturers)) {
      if (!stats[mfr]) stats[mfr] = { rows: 0, hasErrors: false };
      stats[mfr].rows += mfrData.rows;
      if (mfrData.hasErrors) stats[mfr].hasErrors = true;
    }
  }
  return stats;
};

export const buildColumnMappingWarnings = (submissions) => {
  const warnings = {};
  submissions.forEach((s) => {
    Object.entries(s.columnMappingIssues || {}).forEach(([col, { mappedValue }]) => {
      if (!warnings[col]) warnings[col] = [];
      warnings[col].push({ filename: s.filename, mappedValue });
    });
  });
  return warnings;
};

export const buildProcessedData = (submissions, attachment) => {
  if (attachment) return attachment.processedData;
  return submissions.flatMap((s) => s.processedData);
};

export const buildMfrNdcs = (ndcList, selectedManufacturerName) => {
  const codes = ndcList
    .filter((ndc) => !selectedManufacturerName || ndc.manufacturer === selectedManufacturerName)
    .map((ndc) => ndc.code);
  return new Set(codes);
};

export const buildManufacturerFilteredData = (
  data,
  selectedManufacturerName,
  mfrNdcs,
  ndcField
) => {
  if (!selectedManufacturerName) return data;
  return data.filter((row) => mfrNdcs.has(row.data[ndcField]));
};

export const buildEntityOptions = (submissions) => {
  const ids = new Set();
  for (const s of submissions) {
    for (const entity of s.summary.entities ?? []) {
      if (entity.id_340b) ids.add(entity.id_340b);
    }
  }
  return [...ids].sort((a, b) => a.localeCompare(b));
};

export const buildEntityFilteredData = (data, selectedEntity, entityField) => {
  if (!selectedEntity) return data;
  return data.filter((row) => row.data[entityField] === selectedEntity);
};

export const buildManufacturerRowCounts = (data) => {
  const counts = {};
  for (const row of data) {
    if (!row) continue;
    counts[row.manufacturer] = (counts[row.manufacturer] || 0) + 1;
  }
  return counts;
};

export const buildInvalidNdcs = (manufacturerFilteredData, ndcField) => {
  const ndcs = new Set();
  for (const row of manufacturerFilteredData) {
    if (row.validations?.some((v) => v.field === ndcField)) ndcs.add(row.data[ndcField]);
  }
  return ndcs.size;
};

export const buildRowStats = (submissions, selectedManufacturerName) => {
  if (selectedManufacturerName) {
    const mfrSummaries = submissions
      .map((s) => s.summary.manufacturers[selectedManufacturerName])
      .filter(Boolean);
    return {
      rows: mfrSummaries.reduce((acc, s) => acc + s.rows, 0),
      invalid: mfrSummaries.reduce((acc, s) => acc + s.invalid, 0),
      pharmacies: new Set(mfrSummaries.flatMap((s) => s.pharmacies)).size,
      products: new Set(mfrSummaries.flatMap((s) => s.products)).size,
    };
  }
  return {
    rows: submissions.reduce((acc, s) => acc + s.summary.rows, 0),
    invalid: submissions.reduce((acc, s) => acc + s.summary.invalid, 0),
    pharmacies: new Set(submissions.flatMap((s) => s.summary.pharmacies)).size,
    products: new Set(submissions.flatMap((s) => s.summary.products)).size,
  };
};
